import { useEffect, useRef, useState } from 'react'
import {
  foundationChapters,
  discoveryChapters,
  type TourChapter,
  type TourCtx,
} from './chapters'
import styles from './Tour.module.css'

interface TourPillProps {
  ctx: TourCtx
  completed: Record<string, boolean>
  activeChapterId: string | null
  foundationDone: boolean
  onPickChapter: (id: string) => void
  onDismiss: () => void
}

export function TourPill({
  ctx,
  completed,
  activeChapterId,
  foundationDone,
  onPickChapter,
  onDismiss,
}: TourPillProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const foundation = foundationChapters(ctx)
  const discovery = discoveryChapters(ctx)
  const foundationDoneCount = foundation.filter(c => completed[c.id]).length
  const discoveryDoneCount = discovery.filter(c => completed[c.id]).length
  const discoveryPending = discovery.length - discoveryDoneCount

  const inFoundation = !foundationDone
  const allComplete = foundationDone && discoveryPending === 0
  const hasActive = !!activeChapterId

  // Prominent: foundation in progress, OR a chapter is active (resume signal)
  const prominent = (inFoundation || hasActive) && !allComplete

  // Re-key the attention animation when prominent state transitions in,
  // or when activeChapterId changes (e.g. user just closed the player).
  const attentionKey = `${prominent ? 1 : 0}-${activeChapterId ?? ''}-${foundationDoneCount}`
  const [attentionToken, setAttentionToken] = useState(attentionKey)
  useEffect(() => {
    setAttentionToken(attentionKey)
  }, [attentionKey])

  // Inline label content for prominent mode
  const prominentLabel = inFoundation
    ? `Stage ${foundationDoneCount}/${foundation.length}`
    : hasActive
      ? 'Resume'
      : ''

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', handler)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  const handlePick = (ch: TourChapter, locked: boolean) => {
    if (locked) return
    setOpen(false)
    onPickChapter(ch.id)
  }

  const title = inFoundation
    ? `Tour — foundation, ${foundationDoneCount} of ${foundation.length} done`
    : allComplete
      ? 'Tour complete'
      : hasActive
        ? 'Resume tour'
        : `Tour — ${discoveryPending} chapter${discoveryPending === 1 ? '' : 's'} to explore`

  const icon = allComplete
    ? 'bi-check2-circle'
    : inFoundation
      ? 'bi-flag-fill'
      : hasActive
        ? 'bi-play-circle-fill'
        : 'bi-compass'

  return (
    <div ref={wrapRef} className={styles.pillAnchor}>
      <button
        key={attentionToken}
        type="button"
        className={[
          styles.pill,
          open ? styles.pillActive : '',
          allComplete ? styles.pillDone : '',
          prominent ? styles.pillProminent : '',
          prominent ? styles.pillAttention : '',
        ].filter(Boolean).join(' ')}
        onClick={() => setOpen(o => !o)}
        aria-label={title}
        title={title}
      >
        <i className={`bi ${icon}`} />
        {prominent && prominentLabel && (
          <span className={styles.pillLabel}>{prominentLabel}</span>
        )}
        {!prominent && !allComplete && discoveryPending > 0 && !hasActive && (
          <span className={styles.pillBadge}>{discoveryPending}</span>
        )}
      </button>

      {open && (
        <div className={styles.indexPanel} role="dialog" aria-label="Tour chapters">
          <div className={styles.indexHead}>
            <div>
              <h3 className={styles.indexTitle}>
                {inFoundation ? 'Foundation tour' : allComplete ? 'Tour complete' : 'Tour chapters'}
              </h3>
              <div className={styles.indexSubtitle}>
                {inFoundation
                  ? 'Complete these to unlock the rest'
                  : allComplete
                    ? 'You\'ve covered everything'
                    : 'Optional — dive in any order'}
              </div>
            </div>
            <button
              className={styles.headBtn}
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Phase 1: Foundation */}
          {foundation.length > 0 && (
            <>
              <div className={styles.phaseSection}>
                <div className={`${styles.phaseLabel} ${foundationDone ? styles.phaseDone : ''}`}>
                  <span className={styles.phaseTag}>
                    <i className={`bi ${foundationDone ? 'bi-check2-circle' : 'bi-flag'}`} />
                    Foundation
                  </span>
                  <span>{foundationDoneCount} / {foundation.length}</span>
                </div>
                <div className={styles.phaseProgressTrack}>
                  <div
                    className={styles.phaseProgressFill}
                    style={{ width: `${(foundationDoneCount / foundation.length) * 100}%` }}
                  />
                </div>
              </div>
              <div className={styles.indexList}>
                {foundation.map(ch => {
                  const isDone = !!completed[ch.id]
                  const isActive = activeChapterId === ch.id
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      className={`${styles.chapterRow} ${isActive ? styles.active : ''} ${isDone ? styles.completed : ''}`}
                      onClick={() => handlePick(ch, false)}
                    >
                      <span className={styles.chapterRowIcon}>
                        <i className={`bi ${ch.icon}`} />
                      </span>
                      <span className={styles.chapterRowText}>
                        <p className={styles.chapterRowTitle}>
                          {ch.title}
                          {isDone && <i className={`bi bi-check2-circle ${styles.checkmark}`} />}
                        </p>
                        <p className={styles.chapterRowSummary}>{ch.summary}</p>
                      </span>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* Phase 2: Discovery */}
          {discovery.length > 0 && (
            <>
              <div className={styles.phaseSection}>
                <div className={`${styles.phaseLabel} ${!foundationDone ? '' : (discoveryPending === 0 ? styles.phaseDone : '')}`}>
                  <span className={styles.phaseTag}>
                    <i className={`bi ${foundationDone ? 'bi-compass' : 'bi-lock'}`} />
                    More to explore
                  </span>
                  <span>{discoveryDoneCount} / {discovery.length}</span>
                </div>
              </div>
              {!foundationDone && (
                <div className={styles.lockHint}>
                  <i className="bi bi-info-circle" />
                  <span>Finish the foundation tour to unlock these.</span>
                </div>
              )}
              <div className={styles.indexList}>
                {discovery.map(ch => {
                  const isDone = !!completed[ch.id]
                  const isActive = activeChapterId === ch.id
                  const locked = !foundationDone
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      disabled={locked}
                      aria-disabled={locked}
                      className={`${styles.chapterRow} ${isActive ? styles.active : ''} ${isDone ? styles.completed : ''} ${locked ? styles.locked : ''}`}
                      onClick={() => handlePick(ch, locked)}
                    >
                      <span className={styles.chapterRowIcon}>
                        <i className={`bi ${locked ? 'bi-lock-fill' : ch.icon}`} />
                      </span>
                      <span className={styles.chapterRowText}>
                        <p className={styles.chapterRowTitle}>
                          {ch.title}
                          {isDone && <i className={`bi bi-check2-circle ${styles.checkmark}`} />}
                        </p>
                        <p className={styles.chapterRowSummary}>{ch.summary}</p>
                      </span>
                    </button>
                  )
                })}
              </div>
            </>
          )}

          <div className={styles.indexFoot}>
            {allComplete ? (
              <span className={styles.allDone}><i className="bi bi-stars" /> Tour complete</span>
            ) : inFoundation ? (
              <span className={styles.allDone}>Foundation: {foundationDoneCount}/{foundation.length}</span>
            ) : (
              <span className={styles.allDone}>Discovery: {discoveryDoneCount}/{discovery.length}</span>
            )}
            {foundationDone && !allComplete && (
              <button className={styles.dismissLink} onClick={onDismiss}>
                Skip the rest
              </button>
            )}
            {allComplete && (
              <button className={styles.dismissLink} onClick={onDismiss}>
                Hide the tour
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
